import React, { forwardRef, lazy, Suspense, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import "./RealityToDoARCollapse.css";

const ToDoARExperience = lazy(() => import("../../products/todoar/ToDoARExperience"));

const COLLAPSE_STATE_KEY = "__doarCollapseState";
const COLLAPSE_STATES = Object.freeze(["closed", "reveal", "full"]);

function normalizeCollapseState(value) {
  return COLLAPSE_STATES.includes(value) ? value : "closed";
}

function nextCollapseState(state) {
  if (state === "closed") return "reveal";
  if (state === "reveal") return "full";
  return "full";
}

function bodySnapshot(body) {
  return {
    position: body.style.position,
    top: body.style.top,
    left: body.style.left,
    right: body.style.right,
    width: body.style.width,
    maxWidth: body.style.maxWidth,
    overflow: body.style.overflow,
  };
}

function restoreBody(body, snapshot) {
  if (!snapshot) return;
  Object.entries(snapshot).forEach(([key, value]) => {
    body.style[key] = value || "";
  });
}

const RealityToDoARCollapse = forwardRef(function RealityToDoARCollapse(
  { children, journeyContext = null },
  ref
) {
  const [state, setState] = useState(() => (
    typeof window === "undefined"
      ? "closed"
      : normalizeCollapseState(window.history.state?.[COLLAPSE_STATE_KEY])
  ));
  const [hasOpened, setHasOpened] = useState(() => (
    typeof window !== "undefined"
      && normalizeCollapseState(window.history.state?.[COLLAPSE_STATE_KEY]) !== "closed"
  ));
  const rootRef = useRef(null);
  const dragRef = useRef(null);
  const suppressClickRef = useRef(false);
  const lockedScrollRef = useRef(null);
  const bodyStyleRef = useRef(null);

  const pushState = useCallback((next) => {
    const normalized = normalizeCollapseState(next);
    if (normalized !== "closed") setHasOpened(true);
    if (typeof window !== "undefined") {
      window.history.pushState(
        { ...(window.history.state || {}), [COLLAPSE_STATE_KEY]: normalized },
        "",
        window.location.href
      );
    }
    setState(normalized);
  }, []);

  const openReveal = useCallback(() => {
    if (state === "closed") pushState("reveal");
  }, [pushState, state]);

  const openFull = useCallback(() => {
    if (state === "full") return;
    if (state === "closed") {
      window.history.pushState(
        { ...(window.history.state || {}), [COLLAPSE_STATE_KEY]: "reveal" },
        "",
        window.location.href
      );
    }
    pushState("full");
  }, [pushState, state]);

  const stepBack = () => {
    if (state !== "closed" && typeof window !== "undefined") window.history.back();
  };

  const close = useCallback(() => {
    if (typeof window === "undefined" || state === "closed") return;
    window.history.go(state === "full" ? -2 : -1);
  }, [state]);

  useImperativeHandle(ref, () => ({ openReveal, openFull, close, state }), [close, openFull, openReveal, state]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const current = normalizeCollapseState(window.history.state?.[COLLAPSE_STATE_KEY]);
    if (window.history.state?.[COLLAPSE_STATE_KEY] !== current) {
      window.history.replaceState(
        { ...(window.history.state || {}), [COLLAPSE_STATE_KEY]: current },
        "",
        window.location.href
      );
    }
    setState(current);
    if (current !== "closed") setHasOpened(true);

    const onPopState = (event) => {
      const next = normalizeCollapseState(event.state?.[COLLAPSE_STATE_KEY]);
      if (next !== "closed") setHasOpened(true);
      setState(next);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape" && normalizeCollapseState(window.history.state?.[COLLAPSE_STATE_KEY]) !== "closed") {
        window.history.back();
      }
    };
    window.addEventListener("popstate", onPopState);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("popstate", onPopState);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  useEffect(() => {
    if (typeof document === "undefined" || typeof window === "undefined") return;
    const body = document.body;

    if (state !== "closed" && lockedScrollRef.current === null) {
      const scrollY = window.scrollY || window.pageYOffset || 0;
      lockedScrollRef.current = scrollY;
      bodyStyleRef.current = bodySnapshot(body);
      body.style.position = "fixed";
      body.style.top = `-${scrollY}px`;
      body.style.left = "0";
      body.style.right = "0";
      body.style.width = "100%";
      body.style.maxWidth = "100vw";
      body.style.overflow = "hidden";
    } else if (state === "closed" && lockedScrollRef.current !== null) {
      const scrollY = lockedScrollRef.current;
      restoreBody(body, bodyStyleRef.current);
      lockedScrollRef.current = null;
      bodyStyleRef.current = null;
      window.scrollTo(0, scrollY);
    }
  }, [state]);

  useEffect(() => () => {
    if (typeof document === "undefined" || typeof window === "undefined") return;
    if (lockedScrollRef.current !== null) {
      const scrollY = lockedScrollRef.current;
      restoreBody(document.body, bodyStyleRef.current);
      lockedScrollRef.current = null;
      bodyStyleRef.current = null;
      window.scrollTo(0, scrollY);
    }
  }, []);

  const clearDrag = () => {
    dragRef.current = null;
    if (!rootRef.current) return;
    rootRef.current.style.removeProperty("--doar-drag-y");
    rootRef.current.style.removeProperty("--doar-experience-drag-y");
  };

  const onPointerDown = (event) => {
    if (event.button != null && event.button !== 0) return;
    suppressClickRef.current = false;
    dragRef.current = { y: event.clientY, pointerId: event.pointerId };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId || !rootRef.current) return;
    let delta = event.clientY - drag.y;
    if (state === "closed") delta = Math.min(0, delta);
    if (state === "full") delta = Math.max(0, delta);
    delta = Math.max(-180, Math.min(180, delta));
    if (Math.abs(delta) > 7) suppressClickRef.current = true;
    rootRef.current.style.setProperty("--doar-drag-y", `${delta}px`);
    rootRef.current.style.setProperty("--doar-experience-drag-y", `${Math.round(delta * 0.18)}px`);
  };

  const finishPointer = (event, cancelled = false) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    let delta = event.clientY - drag.y;
    if (state === "closed") delta = Math.min(0, delta);
    if (state === "full") delta = Math.max(0, delta);
    clearDrag();

    if (cancelled) return;
    if (delta <= -44 && state !== "full") {
      suppressClickRef.current = true;
      pushState(nextCollapseState(state));
    } else if (delta >= 44 && state !== "closed") {
      suppressClickRef.current = true;
      stepBack();
    }
  };

  const onSeamClick = () => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    if (state === "full") {
      close();
      return;
    }
    pushState(nextCollapseState(state));
  };

  const full = state === "full";
  const open = state !== "closed";
  const context = journeyContext && typeof journeyContext === "object" ? journeyContext : null;

  return (
    <div ref={rootRef} className="reality-collapse" data-collapse-state={state}>
      <div
        className="reality-collapse__experience"
        aria-hidden={full ? "true" : undefined}
        inert={full ? true : undefined}
      >
        {children}
      </div>

      <section
        className="reality-collapse__create"
        aria-label="ToDoAR creation surface"
      >
        <button
          type="button"
          className="reality-collapse__seam"
          aria-expanded={open}
          aria-label={full ? "Return to TheDoAR.com" : state === "reveal" ? "Expand ToDoAR.co" : "Open ToDoAR.co"}
          onClick={onSeamClick}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={(event) => finishPointer(event)}
          onPointerCancel={(event) => finishPointer(event, true)}
        >
          <span className="reality-collapse__line" aria-hidden="true" />
          <span className="reality-collapse__label">
            {full ? "TheDoAR.com" : "ToDoAR.co"}
            <span className="reality-collapse__arrow" aria-hidden="true">{full ? "↓" : "↑"}</span>
          </span>
          <span className="reality-collapse__line" aria-hidden="true" />
        </button>

        <div
          className="reality-collapse__body"
          aria-hidden={state === "closed" ? "true" : undefined}
          inert={state === "closed" ? true : undefined}
        >
          {hasOpened ? (
            <Suspense fallback={<div className="reality-collapse__loading" role="status">Opening ToDoAR…</div>}>
              <ToDoARExperience variant="embedded" journeyContext={context} active={state !== "closed"} />
            </Suspense>
          ) : null}
        </div>
      </section>

      <span className="reality-collapse__sr" aria-live="polite">
        {state === "closed" ? "ToDoAR closed" : state === "reveal" ? "ToDoAR revealed" : "ToDoAR expanded"}
      </span>
    </div>
  );
});

export default RealityToDoARCollapse;
export { COLLAPSE_STATES, COLLAPSE_STATE_KEY, normalizeCollapseState };
